import type { WritingFigure } from '@enghustle/shared'

// Self-contained SVG renderer for IELTS Academic Writing Task 1 figures (FR-029-B).
// Supports bar / line / pie / table. No external charting dependency — pure SVG so
// it stays light and matches the app palette.

const PALETTE = ['#004be2', '#8e00d5', '#006571', '#b45309', '#b41340', '#809bff']
const H: React.CSSProperties = { fontFamily: "'Space Grotesk', sans-serif" }

export default function ChartFigure({ figure }: { figure: WritingFigure }) {
  return (
    <figure style={{ margin: 0, background: 'white', border: '1px solid #d1dcff', borderRadius: '1rem', padding: '1.25rem 1.5rem' }}>
      <figcaption style={{ ...H, fontWeight: 800, fontSize: '0.9375rem', color: '#272e42', marginBottom: '0.25rem' }}>
        {figure.title}
      </figcaption>
      {figure.unit && (
        <div style={{ fontSize: '0.75rem', color: '#a5adc6', marginBottom: '0.75rem' }}>Đơn vị: {figure.unit}</div>
      )}
      <ChartBody figure={figure} />
      <Legend figure={figure} />
    </figure>
  )
}

function ChartBody({ figure }: { figure: WritingFigure }) {
  switch (figure.type) {
    case 'line': return <LineOrBar figure={figure} mode="line" />
    case 'pie': return <Pie figure={figure} />
    case 'table': return <Table figure={figure} />
    case 'bar':
    default: return <LineOrBar figure={figure} mode="bar" />
  }
}

// ─── Bar & Line (shared cartesian grid) ───────────────────────────────────────

function LineOrBar({ figure, mode }: { figure: WritingFigure; mode: 'bar' | 'line' }) {
  const W = 520, Hgt = 240, padL = 44, padB = 32, padT = 12, padR = 12
  const plotW = W - padL - padR, plotH = Hgt - padT - padB
  const cats = figure.categories
  const allVals = figure.series.flatMap((s) => s.values)
  const max = Math.max(1, ...allVals)
  const niceMax = Math.ceil(max / 5) * 5 || max
  const x = (i: number) => padL + (cats.length <= 1 ? plotW / 2 : (plotW / (cats.length - 1)) * i)
  const xBand = (i: number) => padL + (plotW / cats.length) * i
  const y = (v: number) => padT + plotH - (v / niceMax) * plotH

  return (
    <svg viewBox={`0 0 ${W} ${Hgt}`} width="100%" role="img" aria-label={figure.title} style={{ maxWidth: '34rem' }}>
      {/* y grid + labels */}
      {Array.from({ length: 5 }).map((_, i) => {
        const v = (niceMax / 4) * i
        const gy = y(v)
        return (
          <g key={i}>
            <line x1={padL} y1={gy} x2={W - padR} y2={gy} stroke="#eef0ff" strokeWidth={1} />
            <text x={padL - 6} y={gy + 3} textAnchor="end" fontSize={9} fill="#a5adc6">{Math.round(v)}</text>
          </g>
        )
      })}
      {/* x labels */}
      {cats.map((c, i) => (
        <text key={c + i} x={mode === 'bar' ? xBand(i) + plotW / cats.length / 2 : x(i)} y={Hgt - padB + 14}
          textAnchor="middle" fontSize={9} fill="#535b71">{c}</text>
      ))}
      {/* series */}
      {figure.series.map((s, si) => {
        const color = PALETTE[si % PALETTE.length]
        if (mode === 'line') {
          const d = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ')
          return (
            <g key={s.name}>
              <path d={d} fill="none" stroke={color} strokeWidth={2.5} />
              {s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={3} fill={color} />)}
            </g>
          )
        }
        // grouped bars
        const groupW = plotW / cats.length
        const barW = Math.max(4, (groupW * 0.7) / figure.series.length)
        return (
          <g key={s.name}>
            {s.values.map((v, i) => {
              const bx = xBand(i) + groupW * 0.15 + si * barW
              return <rect key={i} x={bx} y={y(v)} width={barW} height={padT + plotH - y(v)} rx={2} fill={color} />
            })}
          </g>
        )
      })}
    </svg>
  )
}

// ─── Pie (single series) ──────────────────────────────────────────────────────

function Pie({ figure }: { figure: WritingFigure }) {
  const values = figure.series[0]?.values ?? []
  const labels = figure.categories
  const total = values.reduce((a, b) => a + b, 0) || 1
  const cx = 110, cy = 110, r = 100
  let angle = -Math.PI / 2
  const slices = values.map((v, i) => {
    const frac = v / total
    const start = angle
    const end = angle + frac * Math.PI * 2
    angle = end
    const large = end - start > Math.PI ? 1 : 0
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start)
    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end)
    return { d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`, color: PALETTE[i % PALETTE.length], pct: Math.round(frac * 100), label: labels[i] }
  })
  return (
    <svg viewBox="0 0 220 220" width="100%" role="img" aria-label={figure.title} style={{ maxWidth: '14rem' }}>
      {slices.map((s, i) => <path key={i} d={s.d} fill={s.color} stroke="white" strokeWidth={1.5} />)}
    </svg>
  )
}

// ─── Table ────────────────────────────────────────────────────────────────────

function Table({ figure }: { figure: WritingFigure }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
        <thead>
          <tr>
            <th style={cell(true)}></th>
            {figure.categories.map((c) => <th key={c} style={cell(true)}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {figure.series.map((s) => (
            <tr key={s.name}>
              <td style={{ ...cell(false), fontWeight: 700 }}>{s.name}</td>
              {s.values.map((v, i) => <td key={i} style={cell(false)}>{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const cell = (head: boolean): React.CSSProperties => ({
  border: '1px solid #eef0f6', padding: '0.4rem 0.625rem', textAlign: head ? 'left' : 'right',
  color: head ? '#535b71' : '#272e42', fontWeight: head ? 700 : 500, background: head ? '#f8f9fd' : 'white',
})

function Legend({ figure }: { figure: WritingFigure }) {
  if (figure.type === 'table') return null
  const items = figure.type === 'pie'
    ? figure.categories.map((c, i) => ({ name: c, color: PALETTE[i % PALETTE.length] }))
    : figure.series.map((s, i) => ({ name: s.name, color: PALETTE[i % PALETTE.length] }))
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem', marginTop: '0.75rem' }}>
      {items.map((it) => (
        <span key={it.name} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem', color: '#535b71' }}>
          <span style={{ width: '0.75rem', height: '0.75rem', borderRadius: '0.2rem', background: it.color }} />
          {it.name}
        </span>
      ))}
    </div>
  )
}
